import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * /students/:id — per-student page. Two tabs:
 *   - "פרטים": basic info in a Descriptions card.
 *   - "יומן פעילות": paginated time-descending feed merged from all
 *     the activity sources the server already indexes
 *     (`/admin/students/:id/activity`).
 *
 * Icons / Hebrew labels for the activity kinds mirror the dashboard
 * "תלמידים אחרונים" card so the UX reads consistently across the
 * two places those strings appear.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Avatar, Button, Card, Descriptions, Empty, Space, Spin, Table, Tabs, Tag, Timeline, Typography, } from 'antd';
import { CheckCircleOutlined, LoginOutlined, PlayCircleOutlined, QuestionCircleOutlined, RiseOutlined, } from '@ant-design/icons';
import { getStudent, getStudentActivity, } from '../api';
import { PageHeader } from '../components/PageHeader';
import StudentProgressCard from './StudentProgressCard';
import { formatAgorotHe, formatHebrewDate, formatRelativeTimeHe, initial } from './dashboard/helpers';
const { Text } = Typography;
const PAGE_SIZE = 50;
export default function StudentPageScreen() {
    const { id: idParam } = useParams();
    const id = Number(idParam);
    const [student, setStudent] = useState(undefined);
    const [loadErr, setLoadErr] = useState(null);
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const s = await getStudent(id);
                if (!cancelled)
                    setStudent(s);
            }
            catch (err) {
                if (!cancelled) {
                    setStudent(null);
                    setLoadErr(err.message || 'שגיאה');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [id]);
    if (student === undefined)
        return _jsx(Spin, { style: { display: 'block', margin: 40 } });
    if (student === null) {
        return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0", breadcrumb: [{ label: 'ניהול' }, { label: 'תלמידים', to: '/users' }, 'לא נמצא'] }), _jsx(Alert, { type: "warning", message: loadErr || 'התלמיד לא נמצא', description: _jsx(Link, { to: "/users", children: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E8\u05E9\u05D9\u05DE\u05EA \u05D4\u05EA\u05DC\u05DE\u05D9\u05D3\u05D9\u05DD" }) })] }));
    }
    const displayName = student.display_name?.trim() || student.email;
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: displayName, breadcrumb: [
                    { label: 'ניהול' },
                    { label: 'תלמידים', to: '/users' },
                    displayName,
                ], avatar: _jsx(Avatar, { size: 44, src: student.avatar_url || undefined, style: { background: '#EFE9FA', color: '#6B21A8', fontWeight: 600 }, children: initial(displayName) }), actions: _jsxs(_Fragment, { children: [_jsx(Text, { type: "secondary", children: student.email }), _jsx(Tag, { color: student.role === 'admin' ? 'red' : undefined, children: student.role === 'admin' ? 'מנהל' : 'תלמיד' })] }) }), _jsx(Tabs, { defaultActiveKey: "details", items: [
                    { key: 'details', label: 'פרטים', children: _jsx(DetailsCard, { student: student }) },
                    { key: 'activity', label: 'יומן פעילות', children: _jsx(ActivityCard, { studentId: student.id }) },
                    { key: 'progress', label: 'התקדמות', children: _jsx(StudentProgressCard, { studentId: student.id }) },
                ] })] }));
}
// ---------------------------------------------------------------------------
// פרטים
// ---------------------------------------------------------------------------
function DetailsCard({ student }) {
    const d = new Date(student.created_at);
    const createdHebrew = Number.isFinite(d.getTime())
        ? `${formatHebrewDate(d)}`
        : student.created_at;
    return (_jsxs(Space, { direction: "vertical", size: 16, style: { width: '100%' }, children: [_jsx(Card, { children: _jsxs(Descriptions, { size: "small", column: 1, bordered: true, children: [_jsx(Descriptions.Item, { label: "\u05E9\u05DD", children: student.display_name || '—' }), _jsx(Descriptions.Item, { label: "\u05DE\u05D9\u05D9\u05DC", children: student.email }), _jsx(Descriptions.Item, { label: "\u05EA\u05E4\u05E7\u05D9\u05D3", children: _jsx(Tag, { color: student.role === 'admin' ? 'red' : undefined, children: student.role === 'admin' ? 'מנהל' : 'תלמיד' }) }), _jsx(Descriptions.Item, { label: "\u05EA\u05D0\u05E8\u05D9\u05DA \u05D4\u05E8\u05E9\u05DE\u05D4", children: createdHebrew }), _jsx(Descriptions.Item, { label: "\u05E9\u05D9\u05E2\u05D5\u05E8\u05D9\u05DD \u05E9\u05D4\u05D5\u05E9\u05DC\u05DE\u05D5", children: student.completed_lessons_count })] }) }), _jsx(EntitlementsCard, { entitlements: student.entitlements }), _jsx(PurchasesCard, { purchases: student.purchases })] }));
}
function EntitlementsCard({ entitlements }) {
    return (_jsx(Card, { title: "\u05D2\u05D9\u05E9\u05D4 \u05DC\u05DB\u05D9\u05EA\u05D5\u05EA", children: entitlements.length === 0
            ? _jsx(Empty, { description: "\u05D0\u05D9\u05DF \u05D2\u05D9\u05E9\u05D4 \u05DC\u05DB\u05D9\u05EA\u05D5\u05EA \u05E2\u05D3\u05D9\u05D9\u05DF", image: Empty.PRESENTED_IMAGE_SIMPLE })
            : (_jsx(Space, { direction: "vertical", size: 8, style: { width: '100%' }, children: entitlements.map((e) => {
                    const purchased = e.purchased_at ? formatHebrewDate(new Date(e.purchased_at)) : '—';
                    const expires = e.expires_at ? formatHebrewDate(new Date(e.expires_at)) : 'ללא תפוגה';
                    return (_jsxs("div", { style: {
                            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                            padding: '6px 10px', borderRadius: 8, background: '#fafafa',
                        }, children: [_jsx(Tag, { color: e.status === 'active' ? 'green' : 'default', children: e.grade_name }), _jsx(Text, { type: "secondary", style: { fontSize: 13 }, children: e.status === 'active' ? 'פעיל' : 'לא פעיל' }), _jsxs(Text, { type: "secondary", style: { fontSize: 12 }, children: ["\u05E0\u05E8\u05DB\u05E9\u05D4: ", purchased, " \u00B7 \u05EA\u05D5\u05E7\u05E3: ", expires, " \u00B7 \u05DE\u05E7\u05D5\u05E8: ", sourceLabel(e.source)] })] }, e.grade_id));
                }) })) }));
}
function sourceLabel(s) {
    switch (s) {
        case 'mock_purchase': return 'רכישה';
        case 'admin': return 'הענקה ידנית';
        case 'promo': return 'קוד שיווקי';
    }
}
function PurchasesCard({ purchases }) {
    const cols = [
        {
            title: 'תאריך', dataIndex: 'purchased_at', width: 170,
            render: (v) => v ? formatHebrewDate(new Date(v)) : '—',
        },
        { title: 'כיתה', dataIndex: 'grade_name' },
        {
            title: 'אמצעי',
            render: (_, r) => r.coupon_code
                ? _jsxs(Tag, { color: "purple", children: ["\u05E7\u05D5\u05E4\u05D5\u05DF ", r.coupon_code] })
                : r.iap_transaction
                    ? _jsx(Text, { type: "secondary", style: { fontSize: 12 }, children: "\u05EA\u05E9\u05DC\u05D5\u05DD" })
                    : _jsx(Text, { type: "secondary", style: { fontSize: 12 }, children: "\u2014" }),
            width: 170,
        },
        {
            title: 'סכום (₪)', dataIndex: 'amount_paid', width: 110,
            render: (v) => formatAgorotHe(v),
        },
    ];
    return (_jsx(Card, { title: "\u05E8\u05DB\u05D9\u05E9\u05D5\u05EA", children: purchases.length === 0
            ? _jsx(Empty, { description: "\u05D0\u05D9\u05DF \u05E8\u05DB\u05D9\u05E9\u05D5\u05EA \u05E2\u05D3\u05D9\u05D9\u05DF", image: Empty.PRESENTED_IMAGE_SIMPLE })
            : _jsx(Table, { size: "small", rowKey: "id", dataSource: purchases, columns: cols, pagination: false }) }));
}
// ---------------------------------------------------------------------------
// יומן פעילות
// ---------------------------------------------------------------------------
const KIND_META = {
    app_open: { label: 'נכנס/ה לאפליקציה', icon: _jsx(LoginOutlined, {}), color: '#8B2FD6' },
    clip_view: { label: 'צפייה בקליפ', icon: _jsx(PlayCircleOutlined, {}), color: '#1849a9' },
    attempt: { label: 'ניסיון במבחן', icon: _jsx(QuestionCircleOutlined, {}), color: '#8a5a00' },
    passed: { label: 'עבר/ה מבחן', icon: _jsx(CheckCircleOutlined, {}), color: '#0a7a52' },
    progress: { label: 'התקדמות בשיעור', icon: _jsx(RiseOutlined, {}), color: '#6B21A8' },
};
function ContextLine({ ctx }) {
    const parts = [ctx.grade, ctx.track, ctx.lesson].filter((p) => !!p);
    if (parts.length === 0)
        return null;
    return (_jsx(Text, { type: "secondary", style: { fontSize: 12 }, children: parts.join(' > ') }));
}
function ActivityCard({ studentId }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const loadMore = useCallback(async () => {
        setLoading(true);
        try {
            const r = await getStudentActivity(studentId, PAGE_SIZE, page * PAGE_SIZE);
            setTotal(r.total);
            setItems((prev) => page === 0 ? r.items : [...prev, ...r.items]);
        }
        catch { /* client auto-surfaces */ }
        finally {
            setLoading(false);
        }
    }, [studentId, page]);
    useEffect(() => { void loadMore(); }, [loadMore]);
    const hasMore = total != null && items.length < total;
    if (!loading && items.length === 0) {
        return (_jsx(Card, { children: _jsx(Empty, { description: "\u05D0\u05D9\u05DF \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA \u05E2\u05D3\u05D9\u05D9\u05DF", image: Empty.PRESENTED_IMAGE_SIMPLE }) }));
    }
    return (_jsxs(Card, { loading: loading && items.length === 0, children: [_jsx("div", { dir: "rtl", style: { direction: 'rtl' }, children: _jsx(Timeline, { items: items.map((it) => {
                        const meta = KIND_META[it.kind];
                        const when = new Date(it.at);
                        const absolute = Number.isFinite(when.getTime())
                            ? when.toLocaleString('he-IL') : it.at;
                        return {
                            dot: _jsx("span", { style: { color: meta.color }, children: meta.icon }),
                            color: meta.color,
                            children: (_jsxs("div", { style: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    textAlign: 'right',
                                    direction: 'rtl',
                                }, children: [_jsx(Text, { strong: true, children: meta.label }), it.label && _jsx(Text, { style: { color: '#22174B' }, children: it.label }), _jsx(ContextLine, { ctx: it.context }), _jsxs(Text, { type: "secondary", style: { fontSize: 12 }, children: [formatRelativeTimeHe(it.at), _jsxs(Text, { type: "secondary", style: { fontSize: 11, marginInlineStart: 8 }, children: ["(", absolute, ")"] })] })] })),
                        };
                    }) }) }), hasMore && (_jsx("div", { style: { textAlign: 'center', marginTop: 12 }, children: _jsx(Button, { onClick: () => setPage((p) => p + 1), loading: loading, children: "\u05D8\u05E2\u05DF \u05E2\u05D5\u05D3" }) }))] }));
}
